import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { SplashScreen } from 'src/components/splash-screen';
import { featureFlagList } from 'src/libs/feature-flags';
import { AttributeSlotsStyle } from 'src/types/attribute-slots';
import { AttributesStyle } from 'src/types/attributes';

/// Provides information about current tenant
const validateTenant = Yup.object({
  tenantId: Yup.string().required()
  // auth provider is validated in other places
}).from('name', 'tenantId');

const validateStrings = Yup.object({
  group: Yup.string().default('Innung'),
  groups: Yup.string().default('Innungen')
});

const validateFeatures = Yup.array(
  Yup.string().required().oneOf(featureFlagList)
);

/// Provides style for current tenant
const validateStyle = Yup.object({
  appName: Yup.string().default('Verbandspartner').required(),
  palette: Yup.object().default({}).required(),
  strings: validateStrings.default({}).required(),

  // TODO: write attribute validation
  slots: Yup.mixed<AttributeSlotsStyle>().default({}).required(),
  attributes: Yup.mixed<AttributesStyle>().default({}).required(),

  userFeatures: validateFeatures.default(['chat']).required(),
  adminFeatures: validateFeatures.default(['chat']).required()
});

export type TenantData = Yup.InferType<typeof validateTenant>;
export type TenantStyle = Yup.InferType<typeof validateStyle>;

export interface TenantState {
  tenant?: TenantData;
  style: TenantStyle;
}

export type TenantContextType = TenantState & {
  setTenant: (tenant?: TenantData) => void;
};

export const TenantContext = createContext<TenantContextType | undefined>(
  undefined
);

/**
 * Provide the tenant information to children
 *
 * NOTE: This component blocks rendering until the tenant context has been
 * initialized properly. Children can rely on the context being ready.
 */
export const TenantProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;

  // custom tenant override
  const [tenantOverride, setTenantOverride] = useState<TenantData>();

  const handleTenant = useCallback(
    (tenant?: TenantData) => setTenantOverride(tenant),
    []
  );

  const context = useMemo(() => {
    try {
      // valid tenant context (with possible overrides)
      return {
        tenant:
          tenantOverride ?? validateTenant.validateSync(globalThis.__TENANT__),
        style: validateStyle.validateSync(globalThis.__STYLE__),
        setTenant: handleTenant
      };
    } catch (error) {
      console.error(error);
      // render error message
      return '__error__';
    }
  }, [tenantOverride, handleTenant]);

  if (context === '__error__')
    return (
      <SplashScreen
        layer="tenant"
        variant="error"
      />
    );

  return (
    <TenantContext.Provider value={context}>{children}</TenantContext.Provider>
  );
};
